import { useEffect, useState, useRef, createContext, useContext } from "react";
import { useParams } from "react-router-dom";
import { motion, useScroll, useSpring, AnimatePresence } from "framer-motion";
import { Play, ExternalLink, ChevronDown, X } from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const BASE = API_URL.replace(/\/api$/, "");

const PULSE_BLUE = "#00B8FF";

const TrailerCtx = createContext(null);
const useTrailer = () => useContext(TrailerCtx);

/** Resolve an /api/uploads/... path to a full URL. */
const r = (p) => (p && p.startsWith("/api/") ? `${BASE}${p}` : p);

/** Parse a YouTube or Vimeo URL into an embeddable iframe src.
 *  Returns null if the URL doesn't look like a known video host. */
function toEmbedUrl(url) {
  if (!url) return null;
  const s = url.trim();
  // YouTube — covers youtu.be/<id>, youtube.com/watch?v=<id>, youtube.com/shorts/<id>, embed/<id>
  const yt = s.match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|shorts\/|embed\/|v\/))([\w-]{6,})/i,
  );
  if (yt) return `https://www.youtube.com/embed/${yt[1]}?autoplay=1&rel=0&modestbranding=1`;
  // Vimeo — vimeo.com/<id> or player.vimeo.com/video/<id>
  const vm = s.match(/(?:vimeo\.com\/(?:video\/)?)([\d]+)/i);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}?autoplay=1&title=0&byline=0&portrait=0`;
  return null;
}

export default function DeckPublicViewer() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [trailer, setTrailer] = useState(null); // {url, title} | null
  const containerRef = useRef(null);

  const { scrollYProgress } = useScroll({ container: containerRef });
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30, restDelta: 0.001 });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r1 = await axios.get(`${API_URL}/api/deck/${token}`);
        if (cancelled) return;
        setData(r1.data);
        // Fire-and-forget view tracking
        axios.post(`${API_URL}/api/deck/${token}/track`).catch(() => {});
        document.title = `Pulse Media · Pitch Deck`;
      } catch (e) {
        if (cancelled) return;
        setError(e.response?.data?.detail || "This share link is invalid or has been revoked.");
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center px-6 text-center">
        <div className="text-xs tracking-widest text-cyan-400 mb-3">/ PULSE MEDIA</div>
        <div className="text-2xl font-bold mb-2">Link unavailable</div>
        <div className="text-zinc-400 max-w-md">{error}</div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-zinc-500 text-sm tracking-widest">LOADING DECK…</div>
      </div>
    );
  }

  const { config, shows, artists, videos, team } = data;

  return (
    <TrailerCtx.Provider value={{ open: (url, title) => setTrailer({ url, title }) }}>
      <div className="bg-black text-white">
      {/* Top progress bar */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-0.5 z-50 origin-left"
        style={{ scaleX, background: PULSE_BLUE }}
      />
      <div
        ref={containerRef}
        className="snap-y snap-mandatory h-screen overflow-y-scroll"
        data-testid="deck-viewer"
      >
        <CoverPage config={config} shows={shows}/>
        <CompanyPage config={config}/>
        {artists.filter(a => a.image).length > 0 && <GalleryPage artists={artists}/>}
        <ReelPage config={config} videos={videos}/>
        <SectionPage
          eyebrow="Pulse Portal"
          headline="A streaming home for our originals."
          body={config.portal_overview}
          bullets={[
            "Subscriber-only originals",
            "Behind-the-scenes from every Pulse production",
            "Exclusive artist drops & live moments",
          ]}
          accent="portal"
        />
        {shows.map((s, i) => <ShowPage key={s.id} show={s} idx={i + 1}/>)}
        <SectionPage
          eyebrow="Casting Portal"
          headline="Open auditions, all year long."
          body={config.casting_portal}
          bullets={[
            "Year-round talent pipeline",
            "Reels, headshots, audio in one place",
            "Producer shortlists + booking workflow",
          ]}
        />
        <SectionPage
          eyebrow="Record Label"
          headline="80+ DSPs. 230+ countries. Monthly payouts."
          body={config.label_reach}
          bullets={[
            "Empire Distribution + Virgin Music partners",
            "Universal-tier reach for indie artists",
            "Royalties collected & paid out every month",
          ]}
        />
        <SectionPage
          eyebrow="Partnerships"
          headline="The machinery behind every release."
          body={config.partnerships}
        />
        {artists.map((a) => <ArtistPage key={a.id} artist={a}/>)}
        {team && team.length > 0 && <TeamPage team={team}/>}
        <ClosingPage config={config}/>
      </div>

      {/* Scroll hint on first page */}
      <ScrollHint container={containerRef}/>

      {/* In-deck trailer player (lightbox) */}
      <TrailerModal trailer={trailer} onClose={() => setTrailer(null)}/>
      </div>
    </TrailerCtx.Provider>
  );
}

/* ----------------------- Trailer modal ----------------------- */
function TrailerModal({ trailer, onClose }) {
  // Close on ESC
  useEffect(() => {
    if (!trailer) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    // Lock body scroll while modal is open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [trailer, onClose]);

  const embed = trailer ? toEmbedUrl(trailer.url) : null;

  return (
    <AnimatePresence>
      {trailer && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8"
          style={{ background: "rgba(0,0,0,0.92)", backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)" }}
          onClick={onClose}
          data-testid="trailer-modal"
        >
          <motion.div
            initial={{ scale: 0.96, y: 10, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.96, y: 10, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="relative w-full max-w-6xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-3 px-1">
              <div>
                <div className="text-cyan-400 text-[10px] font-bold tracking-widest uppercase">/ Trailer</div>
                <div className="font-display text-xl md:text-2xl font-bold tracking-tight text-white">{trailer.title}</div>
              </div>
              <button
                onClick={onClose}
                className="rounded-full w-10 h-10 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                aria-label="Close trailer"
                data-testid="trailer-modal-close"
              >
                <X size={20}/>
              </button>
            </div>

            {/* Player */}
            <div className="relative aspect-video rounded-2xl overflow-hidden border-2 border-cyan-400/60" style={{ boxShadow: "0 0 80px rgba(0,184,255,0.18)" }}>
              {embed ? (
                <iframe
                  src={embed}
                  title={`${trailer.title} trailer`}
                  className="absolute inset-0 w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center bg-zinc-950 p-8">
                  <div className="text-zinc-400 mb-4">This trailer URL isn't embeddable from YouTube or Vimeo.</div>
                  <a
                    href={trailer.url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-black font-bold text-sm"
                    style={{ background: PULSE_BLUE }}
                  >
                    <ExternalLink size={14}/> OPEN IN NEW TAB
                  </a>
                </div>
              )}
            </div>

            <div className="text-center text-zinc-500 text-[10px] mt-3 tracking-widest">PRESS ESC OR CLICK OUTSIDE TO CLOSE</div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ----------------------- Shared visual bits ----------------------- */
function PageShell({ children, eyebrow, footerLabel, dark = true }) {
  return (
    <section className={`snap-start min-h-screen relative ${dark ? "bg-black" : ""} flex flex-col px-8 lg:px-20 py-16`}>
      <div className="flex-1 flex flex-col justify-center">
        {children}
      </div>
      <div className="flex items-center justify-between pt-8 border-t border-cyan-400/15 mt-12">
        <span className="text-[10px] tracking-widest text-white/40">PULSE MEDIA  ·  PITCH DECK 2026</span>
        {footerLabel && <span className="text-[10px] tracking-widest text-white/50 uppercase font-bold">{footerLabel}</span>}
      </div>
    </section>
  );
}

function Eyebrow({ children }) {
  return <div className="text-[10px] font-bold tracking-widest text-cyan-400 uppercase mb-3">/ {children}</div>;
}

function CyanLine({ width = "w-24" }) {
  return <div className={`h-[3px] ${width} mt-3 mb-0`} style={{ background: PULSE_BLUE }}/>;
}

/* ----------------------- 1. COVER ----------------------- */
function CoverPage({ config, shows }) {
  return (
    <section className="snap-start min-h-screen relative overflow-hidden flex items-center justify-center px-8" data-testid="deck-cover">
      {/* Backdrop or fallback to show collage */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${BASE}/api/uploads/deck/cover-backdrop.jpg)` }}
        />
        <div className="absolute inset-0 bg-black/60"/>
        {/* Cyan radial bloom */}
        <div
          className="absolute inset-0"
          style={{ background: `radial-gradient(circle at 50% 55%, rgba(0,184,255,0.18), transparent 55%)` }}
        />
      </div>

      <div className="relative z-10 text-center max-w-3xl">
        <div className="text-cyan-400 font-bold tracking-widest text-xs mb-6">/ PITCH DECK · 2026</div>
        <img
          src={`${BASE}/assets/logo.png`}
          alt="Pulse Media"
          className="h-20 mx-auto mb-6 opacity-95"
          onError={(e) => { e.target.style.display = "none"; }}
        />
        <h1 className="font-display text-6xl md:text-8xl font-bold tracking-tighter mb-4">
          {config.cover_title || "PULSE MEDIA"}
        </h1>
        <div className="text-cyan-400 text-xl md:text-2xl tracking-wide">
          {config.cover_subtitle || "Music. Film. Technology."}
        </div>
        <div className="mt-6 mx-auto w-32 h-px bg-white/30"/>
        <div className="mt-4 text-white/60 text-xs tracking-widest font-bold">
          PARTNERSHIP DECK · {config.cover_year || "2026"}
        </div>
      </div>

      <a
        href={`https://${config.contact_url || "pulsemedia.vip"}`}
        target="_blank" rel="noopener noreferrer"
        className="absolute bottom-8 right-8 text-white/50 text-xs tracking-wider hover:text-cyan-400 transition-colors z-10"
      >
        {config.contact_url || "pulsemedia.vip"}
      </a>
    </section>
  );
}

/* ----------------------- 2. COMPANY ----------------------- */
function CompanyPage({ config }) {
  const pillars = [
    { tag: "MUSIC", title: "Distribution + Label", body: config.music_pillar },
    { tag: "FILM", title: "Original Productions", body: config.film_pillar },
    { tag: "TECHNOLOGY", title: "Pulse Portal", body: config.tech_pillar },
  ];
  return (
    <PageShell footerLabel="Company">
      <Eyebrow>About Pulse Media</Eyebrow>
      <h2 className="font-display text-5xl md:text-6xl font-bold tracking-tighter mb-2 max-w-3xl">
        Reshaping the entertainment industry using talent.
      </h2>
      <CyanLine/>
      <div className="mt-10 grid lg:grid-cols-2 gap-12">
        <p className="text-white/85 text-lg leading-relaxed">{config.company_intro}</p>
        <div className="space-y-4">
          {pillars.map((p) => (
            <div key={p.tag} className="glass-card relative p-5">
              <div className="text-cyan-400 text-[10px] font-bold tracking-widest">/ {p.tag}</div>
              <div className="text-xl font-bold mt-1">{p.title}</div>
              <div className="text-white/70 text-sm mt-2 leading-relaxed">{p.body}</div>
            </div>
          ))}
        </div>
      </div>
    </PageShell>
  );
}

/* Compact fallback: shows initials on a dark cyan-tinged panel if an image fails. */
function ImgOrInitials({ src, alt, className, fallbackName }) {
  const [failed, setFailed] = useState(!src);
  const initials = (fallbackName || "")
    .split(/\s+/).filter(Boolean).slice(0, 2)
    .map((w) => w[0]).join("").toUpperCase() || "?";
  if (failed) {
    return (
      <div className={`${className} flex items-center justify-center bg-gradient-to-br from-zinc-900 to-zinc-950 relative overflow-hidden`}>
        <div className="absolute inset-0" style={{ background: `radial-gradient(circle at 50% 30%, rgba(0,184,255,0.15), transparent 60%)` }}/>
        <span className="relative font-display text-4xl md:text-5xl font-bold text-white/30 tracking-tight">{initials}</span>
      </div>
    );
  }
  return (
    <img
      src={src} alt={alt}
      className={className}
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}

/* ----------------------- 3. GALLERY ----------------------- */
function GalleryPage({ artists }) {
  const photos = artists.filter(a => a.image).slice(0, 8);
  return (
    <PageShell footerLabel="Roster">
      <Eyebrow>Roster Gallery</Eyebrow>
      <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tighter">The artists.</h2>
      <CyanLine/>
      <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-5">
        {photos.map((a) => (
          <div key={a.id} className="relative aspect-[3/4] rounded-lg overflow-hidden border border-cyan-400/30 group">
            <ImgOrInitials
              src={r(a.image)}
              alt={a.name}
              fallbackName={a.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black via-black/60 to-transparent"/>
            <div className="absolute bottom-3 left-3 text-sm font-bold">{a.name}</div>
          </div>
        ))}
      </div>
    </PageShell>
  );
}

/* ----------------------- 4. REEL ----------------------- */
function ReelPage({ config, videos }) {
  const trailerCtx = useTrailer();
  const yt = (config.reel_youtube_id || "").trim();
  const carousel = (videos || []).slice(0, 6);
  return (
    <PageShell footerLabel="Reel">
      <Eyebrow>Show Reel</Eyebrow>
      <h2 className="font-display text-4xl md:text-6xl font-bold tracking-tighter">From our reel.</h2>
      <CyanLine/>
      <p className="text-white/78 text-base mt-4 max-w-3xl leading-relaxed">{config.reel_intro}</p>

      <div className="mt-8 relative rounded-xl overflow-hidden border border-cyan-400/50 max-w-5xl">
        <div className="aspect-video bg-black/40">
          {yt ? (
            <button
              type="button"
              onClick={() => trailerCtx?.open(`https://youtu.be/${yt}`, "Pulse Media 2024 Show Reel")}
              className="block group relative w-full h-full text-left"
              data-testid="deck-reel-play"
            >
              <img src={`https://i.ytimg.com/vi/${yt}/maxresdefault.jpg`} alt="reel" className="w-full h-full object-cover" onError={(e) => { e.target.src = `https://i.ytimg.com/vi/${yt}/hqdefault.jpg`; }}/>
              <div className="absolute inset-0 bg-black/15 group-hover:bg-black/5 transition-colors flex items-center justify-center">
                <div className="w-20 h-20 rounded-full bg-cyan-400 flex items-center justify-center shadow-2xl shadow-cyan-400/60 group-hover:scale-110 transition-transform">
                  <Play size={32} className="text-black ml-1" fill="currentColor"/>
                </div>
              </div>
            </button>
          ) : <div className="w-full h-full bg-zinc-900"/>}
        </div>
      </div>

      {carousel.length > 0 && (
        <div className="mt-8">
          <div className="text-cyan-400 text-[10px] font-bold tracking-widest mb-3">/ FROM THE CATALOG</div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
            {carousel.map((v) => (
              <button
                key={v.id} type="button"
                onClick={() => trailerCtx?.open(`https://youtu.be/${v.youtube_id}`, v.title || "Music Video")}
                className="relative aspect-video rounded-md overflow-hidden border border-cyan-400/30 group text-left"
              >
                <img src={`https://i.ytimg.com/vi/${v.youtube_id}/hqdefault.jpg`} alt={v.title} className="w-full h-full object-cover"/>
                <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/95 to-transparent"/>
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                  <Play size={20} className="text-cyan-400" fill="currentColor"/>
                </div>
                <div className="absolute bottom-1 left-1.5 right-1.5 text-[9px] font-bold leading-tight">{(v.title || "").slice(0, 32)}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </PageShell>
  );
}

/* ----------------------- 5. PORTAL / CASTING / SECTION ----------------------- */
function SectionPage({ eyebrow, headline, body, bullets, accent }) {
  return (
    <PageShell footerLabel={eyebrow}>
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2 className="font-display text-4xl md:text-6xl font-bold tracking-tighter max-w-4xl">{headline}</h2>
      <CyanLine/>
      <div className="mt-8 grid lg:grid-cols-2 gap-12">
        <p className="text-white/82 text-lg leading-relaxed">{body}</p>
        {bullets && (
          <div className="glass-card p-6 self-start">
            {bullets.map((b) => (
              <div key={b} className="flex gap-3 py-2">
                <span className="text-cyan-400 font-bold">—</span>
                <span className="text-white">{b}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </PageShell>
  );
}

/* ----------------------- 6-10. SHOW PAGES ----------------------- */
function ShowPage({ show, idx }) {
  const trailerCtx = useTrailer();
  const detailRows = [
    { label: "Genre", value: show.genre },
    { label: "Runtime", value: show.runtime },
    { label: "Year", value: show.year },
    { label: "Director", value: show.director },
    { label: "Starring", value: show.starring, full: true },
  ].filter((r) => r.value && String(r.value).trim());

  return (
    <PageShell footerLabel={`Show · ${show.title || ""}`}>
      <div className="grid lg:grid-cols-[auto_1fr] gap-12 items-start">
        {/* Glossy 9:16 poster — positioned lower so it doesn't crowd the title */}
        <div className="relative w-[260px] md:w-[300px] mt-12 md:mt-16" data-testid={`deck-show-poster-${show.id || idx}`}>
          {/* Cyan glow */}
          <div className="absolute -inset-3 rounded-2xl pointer-events-none" style={{ boxShadow: "0 0 80px rgba(0,184,255,0.25)" }}/>
          <div className="relative aspect-[9/16] rounded-xl overflow-hidden border-2 border-cyan-400/80 bg-zinc-950">
            {show.poster ? (
              <ImgOrInitials src={r(show.poster)} alt={show.title} fallbackName={show.title} className="w-full h-full object-cover bg-black"/>
            ) : <div className="w-full h-full bg-zinc-900"/>}
            {/* Glossy top sheen */}
            <div className="absolute inset-x-0 top-0 h-1/3 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.18), transparent)" }}/>
          </div>
          {/* Reflection on shelf */}
          {show.poster && (
            <div className="mt-1 aspect-[9/16] max-h-32 overflow-hidden relative" style={{ maskImage: "linear-gradient(180deg, rgba(0,0,0,0.65), transparent)", WebkitMaskImage: "linear-gradient(180deg, rgba(0,0,0,0.65), transparent)" }}>
              <img src={r(show.poster)} alt="" className="w-full h-full object-cover transform scale-y-[-1] opacity-90" aria-hidden="true" loading="lazy" onError={(e) => { e.target.style.display = "none"; }}/>
            </div>
          )}
        </div>

        {/* Copy column */}
        <div className="max-w-2xl">
          <Eyebrow>Portal Original · {String(idx).padStart(2, "0")}</Eyebrow>
          <h2 className="font-display text-5xl md:text-7xl font-bold tracking-tighter">{show.title || "Untitled"}</h2>
          {show.tagline && <div className="text-cyan-400 italic text-xl mt-2">{show.tagline}</div>}
          <CyanLine/>

          <p className="text-white/85 text-base mt-5 leading-relaxed">{show.synopsis}</p>
          {show.extended_synopsis && (
            <div className="mt-4 text-white/72 text-sm whitespace-pre-line leading-relaxed">{show.extended_synopsis}</div>
          )}

          {/* Project details — metadata grid under the description */}
          {detailRows.length > 0 && (
            <div className="mt-8 grid grid-cols-2 gap-x-8 gap-y-3 pt-6 border-t border-white/10" data-testid={`deck-show-details-${show.id || idx}`}>
              {detailRows.map((row) => (
                <div key={row.label} className={row.full ? "col-span-2" : ""}>
                  <div className="text-cyan-400 text-[10px] font-bold tracking-widest uppercase">{row.label}</div>
                  <div className="text-white text-sm mt-1 leading-snug">{row.value}</div>
                </div>
              ))}
            </div>
          )}

          {/* Action buttons — trailer + portal */}
          {(show.trailer_url || show.link) && (
            <div className="mt-7 flex flex-wrap gap-3">
              {show.trailer_url && (
                <button
                  type="button"
                  onClick={() => trailerCtx?.open(show.trailer_url, show.title || "Trailer")}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm border-2 border-cyan-400 text-cyan-400 hover:bg-cyan-400 hover:text-black transition-colors"
                  data-testid={`deck-show-trailer-${show.id || idx}`}
                >
                  <Play size={14} fill="currentColor"/> WATCH TRAILER
                </button>
              )}
              {show.link && (
                <a
                  href={show.link} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-black font-bold text-sm hover:scale-105 transition-transform"
                  style={{ background: PULSE_BLUE }}
                  data-testid={`deck-show-portal-${show.id || idx}`}
                >
                  <Play size={14} fill="currentColor"/> WATCH ON PORTAL <ExternalLink size={12}/>
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </PageShell>
  );
}

/* ----------------------- ARTIST PAGES ----------------------- */
function ArtistPage({ artist }) {
  const socials = [
    ["Spotify", artist.spotify],
    ["Apple Music", artist.apple_music],
    ["YouTube", artist.youtube],
    ["Instagram", artist.instagram],
    ["Facebook", artist.facebook],
  ].filter(([, v]) => v);

  return (
    <section className="snap-start min-h-screen grid lg:grid-cols-2 relative bg-black" data-testid={`deck-artist-${artist.id}`}>
      {/* Photo */}
      <div className="relative min-h-[40vh] lg:min-h-screen">
        {artist.image ? (
          <ImgOrInitials
            src={r(artist.image)}
            alt={artist.name}
            fallbackName={artist.name}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-zinc-900 to-zinc-950 flex items-center justify-center">
            <span className="font-display text-7xl font-bold text-white/15 tracking-tight">
              {(artist.name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase()}
            </span>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/40 lg:to-black/0"/>
        {/* Cyan vertical accent */}
        <div className="absolute top-0 right-0 bottom-0 w-[3px] hidden lg:block" style={{ background: PULSE_BLUE }}/>
      </div>

      {/* Content */}
      <div className="flex flex-col justify-center px-8 lg:px-16 py-16">
        <Eyebrow>Artist</Eyebrow>
        <h2 className="font-display text-5xl md:text-6xl font-bold tracking-tighter">{artist.name}</h2>
        {artist.tagline && <div className="text-cyan-400 italic text-lg mt-2">{artist.tagline}</div>}
        <div className="h-px bg-white/20 my-4 max-w-md"/>
        {artist.bio && <p className="text-white/78 text-sm leading-relaxed max-w-md">{artist.bio}</p>}

        {socials.length > 0 && (
          <div className="mt-5 max-w-md">
            <div className="text-cyan-400 text-[10px] font-bold tracking-widest mb-2">/ LISTEN & FOLLOW</div>
            <div className="space-y-1.5">
              {socials.map(([label, url]) => (
                <a key={label} href={url} target="_blank" rel="noopener noreferrer" className="block text-sm hover:text-cyan-400 transition-colors truncate">
                  {label} <span className="text-cyan-400">▸</span> <span className="text-white/60">{url}</span>
                </a>
              ))}
            </div>
          </div>
        )}

        {artist.slug && (
          <a href={`https://pulsemedia.vip/${artist.slug}`} target="_blank" rel="noopener noreferrer"
             className="mt-4 inline-flex text-cyan-400 font-bold text-sm hover:underline">
            ▸ pulsemedia.vip/{artist.slug}
          </a>
        )}
      </div>
    </section>
  );
}

/* ----------------------- TEAM PAGE ----------------------- */
function TeamPage({ team }) {
  // Auto-tune card size so everything fits on one page regardless of count.
  // 1-4 → big cards; 5-8 → mid; 9+ → compact 4-up.
  const n = team.length;
  const cols = n <= 4 ? Math.min(n, 4) : n <= 6 ? 3 : 4;
  return (
    <PageShell footerLabel="Team">
      <Eyebrow>The Team</Eyebrow>
      <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tighter">The humans behind Pulse.</h2>
      <CyanLine/>
      <div
        className={`mt-10 grid gap-5 ${cols === 4 ? "grid-cols-2 md:grid-cols-4" : cols === 3 ? "grid-cols-2 md:grid-cols-3" : "grid-cols-2 md:grid-cols-4"}`}
        data-testid="deck-team-grid"
      >
        {team.map((m) => (
          <div
            key={m.id}
            className="glass-card relative p-0 overflow-hidden border border-cyan-400/25"
            data-testid={`deck-team-member-${m.id}`}
          >
            <div className="relative aspect-[4/5] bg-zinc-950">
              <ImgOrInitials
                src={r(m.image)}
                alt={m.name}
                fallbackName={m.name}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black via-black/70 to-transparent"/>
              {/* Cyan corner accent */}
              <div className="absolute top-0 left-0 w-10 h-[2px]" style={{ background: PULSE_BLUE }}/>
              <div className="absolute top-0 left-0 h-10 w-[2px]" style={{ background: PULSE_BLUE }}/>
            </div>
            <div className="p-4">
              <div className="text-cyan-400 text-[9px] font-bold tracking-widest uppercase">
                {m.role || "Team"}
              </div>
              <div className="font-display font-bold tracking-tight text-base md:text-lg mt-1 leading-tight">
                {m.name}
              </div>
              {m.bio && (
                <div className="text-white/65 text-[11px] mt-2 leading-snug line-clamp-3">
                  {m.bio}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </PageShell>
  );
}


/* ----------------------- CLOSING ----------------------- */
function ClosingPage({ config }) {
  return (
    <section className="snap-start min-h-screen flex flex-col items-center justify-center text-center px-8 relative overflow-hidden bg-black" data-testid="deck-closing">
      <div
        className="absolute inset-0"
        style={{ background: `radial-gradient(circle at 50% 50%, rgba(0,184,255,0.15), transparent 60%)` }}
      />
      <div className="relative z-10 max-w-3xl">
        <img src={`${BASE}/assets/logo.png`} alt="Pulse Media" className="h-20 mx-auto mb-8 opacity-95" onError={(e) => { e.target.style.display = "none"; }}/>
        <h2 className="font-display text-4xl md:text-6xl font-bold tracking-tighter">{config.closing_headline}</h2>
        <div className="mx-auto w-24 h-[3px] my-6" style={{ background: PULSE_BLUE }}/>
        <p className="text-white/78 max-w-2xl mx-auto leading-relaxed">{config.closing_body}</p>
        <div className="mt-10">
          <a href={`mailto:${config.contact_email}`} className="text-cyan-400 text-xl md:text-2xl font-bold hover:underline block">
            {config.contact_email}
          </a>
          <a href={`https://${config.contact_url}`} target="_blank" rel="noopener noreferrer" className="text-white text-lg mt-2 inline-block hover:text-cyan-400 transition-colors">
            {config.contact_url}
          </a>
        </div>
        <img src={`${BASE}/api/uploads/deck/partners.png`} alt="" className="mt-12 max-w-md mx-auto opacity-80" onError={(e) => { e.target.style.display = "none"; }}/>
        <div className="text-white/30 text-[10px] mt-8 tracking-wider">
          © Pulse Media — Music. Film. Technology.   Distribution via Origin Audio, SE Empire, Virgin Music.
        </div>
      </div>
    </section>
  );
}

/* ----------------------- Scroll-down hint ----------------------- */
function ScrollHint({ container }) {
  const [hide, setHide] = useState(false);
  useEffect(() => {
    const el = container.current;
    if (!el) return;
    const onScroll = () => { if (el.scrollTop > 80) setHide(true); };
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [container]);
  if (hide) return null;
  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 text-cyan-400 text-xs tracking-widest font-bold flex flex-col items-center gap-2 animate-bounce pointer-events-none z-40">
      SCROLL
      <ChevronDown size={18}/>
    </div>
  );
}
